import { showHttpError, showSuccess } from '@calendly/alert'
import { parseQuery } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { omitBy, isNil } from 'lodash-es'

import { setGetParams } from 'actions/core/navigation'
import { fetchCurrentSubscription } from 'features/billing'
import { applyManagedEvents } from 'features/managed_event_types'
import { fetchStatistics } from 'features/organization_statistics'
import { idSelector } from 'selectors/current_user'

import {
  getMemberships,
  changeMembershipRoles,
  setOwnership,
  deleteUser,
  deleteMembership,
  batchDeleteUsers,
  batchDeleteMemberships
} from '../api'
import {
  sliceSelector,
  paginationSelector,
  changeRolePopupMembershipsSelector,
  filterTermSelector,
  sortFieldSelector,
  sortOrderSelector,
  membershipToRemoveSelector,
  selectedIdsSelector,
  selectedMembershipsSelector,
} from './selectors'

export const fetchMemberships = createAsyncThunk(
  'usersPage/fetchMemberships',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const state = getState()
    const page = paginationSelector(state).currentPage
    const filter = filterTermSelector(state)

    const sortField = sortFieldSelector(state)
    const sortOrder = sortOrderSelector(state)
    const [success, response] = await getMemberships(
      omitBy({ page, filter_term: filter, sort_field: sortField, sort_order: sortOrder }, isNil)
    )

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue([])
    }

    return { ...response }
  }
)

const initialState = {
  memberships: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0
  },
  filter: '',
  sortField: 'name',
  sortOrder: 'asc',
  isLoading: true,
  selectedMemberships: [],
  changeRolePopup: {
    memberships: [],
    isOpen: false
  },
  removeUserPopup: {
    isOpen: false,
    isBatch: false,
    status: 'idle',
    userId: null,
    membershipUuid: null,
  },
  addToGroupPopupOpen: false,
}

const slice = createSlice({
  name: 'usersPage/active',
  initialState,
  reducers: {
    resetSelection: (state, { payload: memberships }) => {
      const { selectedMemberships } = state
      const ids = memberships.map(({ id }) => id)
      state.selectedMemberships = selectedMemberships.filter(({ id }) => !ids.includes(id))
    },
    toggleSelection: (state, { payload: membership }) => {
      if (state.selectedMemberships.some(({ id }) => id === membership.id)) {
        state.selectedMemberships = state.selectedMemberships.filter(({ id }) => id !== membership.id)
      } else {
        state.selectedMemberships.push(membership)
      }
    },
    addSelectedMemberships: (state, { payload: memberships }) => {
      state.selectedMemberships.push(...memberships)
    },
    clearSelection: (state) => {
      state.selectedMemberships = []
    },
    setPage: (state, { payload }) => {
      state.pagination.currentPage = payload
    },
    setFilter: (state, { payload }) => {
      state.filter = payload
    },
    openBatchChangeRolePopup(state) {
      state.changeRolePopup.isOpen = true
      state.changeRolePopup.memberships = state.selectedMemberships
    },
    openChangeRolePopup(state, action) {
      state.changeRolePopup.isOpen = true
      state.changeRolePopup.memberships = [action.payload]
    },
    closeChangeRolePopup(state) {
      state.changeRolePopup.isOpen = false
      state.changeRolePopup.isOwnershipWarningOpen = false
      state.changeRolePopup.memberships = []
    },
    openOwnershipWarningPopup(state) {
      state.changeRolePopup.isOpen = true
      state.changeRolePopup.isOwnershipWarningOpen = true
    },
    removingStarted(state) {
      state.removeUserPopup.status = 'loading'
    },
    removingCompleted(state) {
      state.removeUserPopup.status = 'idle'
    },
    openRemoveUserPopup(state, action) {
      state.removeUserPopup.isOpen = true
      state.removeUserPopup.userId = action.payload.userId
      state.removeUserPopup.membershipUuid = action.payload.membershipUuid
    },
    openBatchRemoveUserPopup(state) {
      state.removeUserPopup.isOpen = true
      state.removeUserPopup.isBatch = true
    },
    closeRemoveUserPopup(state) {
      state.removeUserPopup.isOpen = false
      state.removeUserPopup.isBatch = false
      state.removeUserPopup.userId = null
      state.removeUserPopup.userUuid = null
    },
    openAddToGroupPopup(state) {
      state.addToGroupPopupOpen = true
    },
    closeAddToGroupPopup(state) {
      state.addToGroupPopupOpen = false
    },
    setSortOrder(state, { payload }) {
      state.sortOrder = payload
    },
    setSortField(state, { payload }) {
      state.sortField = payload
    },
    sortBy(state, { payload }) {
      if (state.sortField === payload) {
        state.sortOrder = state.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        state.sortOrder = 'asc'
      }

      state.sortField = payload
    },
    resetState() {
      return initialState
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMemberships.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(fetchMemberships.fulfilled, (state, { payload }) => {
      state.memberships = payload.results
      state.pagination = {
        currentPage: payload.pagination.current_page,
        totalPages: payload.pagination.total_pages,
        totalCount: payload.pagination.total_count,
        pageSize: payload.pagination.page_size
      }
      state.isLoading = false
    })
    builder.addCase(fetchMemberships.rejected, (state) => {
      state.isLoading = false
    })
    builder.addCase(applyManagedEvents.fulfilled, (state) => {
      state.selectedMemberships = []
    })
  }
})

export const togglePageSelection = () => (dispatch, getState) => {
  const state = getState()
  const { memberships: entities } = sliceSelector(state)
  const currentUserId = idSelector(state)
  const selectedIds = selectedIdsSelector(state)

  const selectableMemberships = entities.filter(({ role, user_id: userId }) => (
    role !== 'owner' && userId !== currentUserId
  ))
  const remainingMemberships = selectableMemberships.filter(({ id }) => !selectedIds.includes(id))

  if (remainingMemberships.length) {
    dispatch(slice.actions.addSelectedMemberships(remainingMemberships))
  } else {
    dispatch(slice.actions.resetSelection(selectableMemberships))
  }
}

export const changeOwnership = () => async (dispatch, getState) => {
  const state = getState()
  const { user_id: userId } = changeRolePopupMembershipsSelector(state)[0]
  const [success, response] = await setOwnership(userId)

  if (!success) {
    dispatch(showHttpError(response))
    return success
  }

  dispatch(slice.actions.closeChangeRolePopup())
  dispatch(fetchMemberships())
  return success
}

export const changeRoles = (role) => async (dispatch, getState) => {
  const state = getState()
  const ids = changeRolePopupMembershipsSelector(state).map((member) => member.id)
  const [success, response] = await changeMembershipRoles(ids, role)

  if (!success) {
    dispatch(showHttpError(response))
    return success
  }

  dispatch(slice.actions.closeChangeRolePopup())
  dispatch(slice.actions.clearSelection())
  dispatch(fetchMemberships())
  return success
}

export const updatePage = ({ page }) => (dispatch) => {
  dispatch(setGetParams({ page }))
  dispatch(slice.actions.setPage(page))
  dispatch(fetchMemberships())
}

export const initPage = () => async (dispatch) => {
  const params = parseQuery(window.location.search)
  const page = params.page ? parseInt(params.page, 10) : 1
  dispatch(slice.actions.setPage(page))
  if (params.sort_field) dispatch(slice.actions.setSortField(params.sort_field))
  if (params.sort_order) dispatch(slice.actions.setSortOrder(params.sort_order))

  const { error, payload: results } = await dispatch(fetchMemberships())

  if (error) return

  if (results.pagination.is_out_of_range) {
    await dispatch(updatePage({ page: 1 }))
  }
}

export const search = (query) => (dispatch) => {
  dispatch(slice.actions.setFilter(query))
  dispatch(slice.actions.setPage(1))
  dispatch(setGetParams({ page: null }))
  dispatch(fetchMemberships())
}

export const removeUserAndCloseModal = () => async (dispatch, getState) => {
  const state = getState()
  const membershipToRemove = membershipToRemoveSelector(state)
  await dispatch(slice.actions.removingStarted())
  const [success, response] = await deleteMembership(membershipToRemove.id)

  if (!success) {
    dispatch(showHttpError(response))
    dispatch(slice.actions.removingCompleted())
    return success
  }

  dispatch(fetchMemberships())
  dispatch(fetchStatistics())
  dispatch(slice.actions.closeRemoveUserPopup())
  dispatch(slice.actions.removingCompleted())
  dispatch(fetchCurrentSubscription())
  const flashMessage = I18n.t('publisher.organization.members.removed_success', { name: membershipToRemove.name })
  dispatch(showSuccess(flashMessage))

  return success
}

export const deleteUserAndCloseModal = () => async (dispatch, getState) => {
  const state = getState()
  const membershipToRemove = membershipToRemoveSelector(state)
  await dispatch(slice.actions.removingStarted())
  const [success, response] = await deleteUser(membershipToRemove.user_id)

  if (!success) {
    dispatch(showHttpError(response))
    dispatch(slice.actions.removingCompleted())
    return success
  }

  dispatch(fetchMemberships())
  dispatch(fetchStatistics())
  dispatch(slice.actions.closeRemoveUserPopup())
  await dispatch(slice.actions.removingCompleted())
  dispatch(fetchCurrentSubscription())
  const flashMessage = I18n.t('publisher.organization.members.removed_and_deleted_success', { name: membershipToRemove.name })
  dispatch(showSuccess(flashMessage))

  return success
}

export const batchRemoveUsersAndCloseModal = () => async (dispatch, getState) => {
  const state = getState()
  const ids = selectedIdsSelector(state)
  await dispatch(slice.actions.removingStarted())
  const [success, response] = await batchDeleteMemberships(ids)

  if (!success) {
    dispatch(showHttpError(response))
    dispatch(slice.actions.removingCompleted())
    return success
  }

  dispatch(fetchMemberships())
  dispatch(fetchStatistics())
  dispatch(slice.actions.clearSelection())
  dispatch(slice.actions.closeRemoveUserPopup())
  dispatch(slice.actions.removingCompleted())
  dispatch(fetchCurrentSubscription())
  const flashMessage = I18n.t('publisher.organization.members.batch_remove.removed_success', { count: ids.length })
  dispatch(showSuccess(flashMessage))

  return success
}

export const batchDeleteUsersAndCloseModal = () => async (dispatch, getState) => {
  const state = getState()
  const userIds = selectedMembershipsSelector(state).map((membership) => membership.user_id)
  await dispatch(slice.actions.removingStarted())
  const [success, response] = await batchDeleteUsers(userIds)

  if (!success) {
    dispatch(slice.actions.removingCompleted())
    dispatch(showHttpError(response))
    return success
  }

  dispatch(fetchMemberships())
  dispatch(fetchStatistics())
  dispatch(slice.actions.clearSelection())
  dispatch(slice.actions.closeRemoveUserPopup())
  dispatch(slice.actions.removingCompleted())
  dispatch(fetchCurrentSubscription())
  const flashMessage = I18n.t('publisher.organization.members.batch_remove.removed_and_deleted_success', { count: userIds.length })
  dispatch(showSuccess(flashMessage))

  return success
}

export const sortUsers = (field) => async (dispatch, getState) => {
  dispatch(slice.actions.sortBy(field))
  dispatch(slice.actions.setPage(1))
  const state = getState()
  const sortOrder = sortOrderSelector(state)
  const sortField = sortFieldSelector(state)
  dispatch(setGetParams({ sort_field: sortField, sort_order: sortOrder, page: null }))
  await dispatch(fetchMemberships())
}

export const { reducer, actions: {
  toggleSelection,
  clearSelection,
  openChangeRolePopup,
  openBatchChangeRolePopup,
  closeChangeRolePopup,
  openOwnershipWarningPopup,
  closeRemoveUserPopup,
  openRemoveUserPopup,
  openBatchRemoveUserPopup,
  openAddToGroupPopup,
  closeAddToGroupPopup,
  resetState
} } = slice
